<template>
  <div v-if="electionPeriod">

    <icon-router-link icon="chevron-left" to="/pk/forside">Tilbake</icon-router-link>

    <!-- title -->

    <div class="uppercase font-bold text-gray-500 mt-6">Valgperiode: {{ electionPeriod.name }}</div>


    <!-- header -->

    <div class="flex justify-between items-center mb-4">
      <h1 class="text-4xl font-bold tracking-tight text-gray-600">Resultater</h1>
      <div class="flex items-center">
      <select class="bg-white flex ml-4" :class="{ 'text-gray-500': gotSlot === '' }" v-model="gotSlot">
          <option :value="null" class="text-gray-900">Fått plass?</option>
          <option :value="true" class="text-gray-900">Ja</option>
          <option :value="false" class="text-gray-900">Nei</option>
        </select>
        <button class="btn btn-primary flex items-center ml-4" @click.prevent="onExport" v-if="items.length > 0" title="Last ned Excel regneark med resultater">
          <font-awesome-icon :icon="['far', 'file-excel']" />
          <div class="ml-2">Resultater</div>
        </button>
        <button class="btn btn-primary flex items-center ml-4" @click.prevent="onExportAvailable" v-if="items.length > 0" title="Last ned Excel regneark med fortsatt ledige praksisplasser">
          <font-awesome-icon :icon="['far', 'file-excel']" />
          <div class="ml-2">Ledige praksisapotek</div>
        </button>
      </div>
    </div>


    <!-- body -->

    <panel class="p-4 mb-8">
      <data-table
        class="mt-4"
        :headers="headers"
        @click="onClick"
        :items="filteredItems">
      </data-table>
    </panel>


    <!-- modal -->

    <modal v-if="selected" header="Detaljer" @close="selected = null">

      <div class="flex items-start">

        <div>
          <title-slot title="Student">
            <div>{{ selected.student.firstName }} {{ selected.student.lastName }}</div>
            <div class="underline text-red-500" v-if="selected.student.phone"><a :href="'tel:' + selected.student.phone">{{ selected.student.phone }}</a></div>
            <div class="underline text-red-500" v-if="selected.student.email"><a :href="'mailto:' + selected.student.email">{{ selected.student.email }}</a></div>
          </title-slot>
          <title-slot class="mt-6" title="Plass i trekningen">
            {{ selected.student.position }}
          </title-slot>
          <title-slot class="mt-6" title="Praksisperiode">
            <div>{{ selected.practicePeriod.name }}</div>
            <div>{{ selected.practicePeriod.studyDisplay }}</div>
            <div>Fra: {{ selected.practicePeriod.fromDisplay }}</div>
            <div>Til: {{ selected.practicePeriod.toDisplay }}</div>
          </title-slot>
          <title-slot class="mt-6" title="Særplass?">
            {{ selected.isSpecial ? 'Ja' : 'Nei' }}
          </title-slot>
        </div>

        <div class="pl-16">
          <title-slot title="Tildelt apotek">
            <template v-if="selected.pharmacy">
              <div>{{ selected.pharmacy.name }}</div>
              <div class="underline text-red-500" v-if="selected.pharmacy.user && selected.pharmacy.user.phone"><a :href="'tel:' + selected.pharmacy.user.phone">{{ selected.pharmacy.user.phone }}</a></div>
              <div class="underline text-red-500" v-if="selected.pharmacy.user && selected.pharmacy.user.email"><a :href="'mailto:' + selected.pharmacy.user.email">{{ selected.pharmacy.user.email }}</a></div>
            </template>
            <template v-else>
              <div>Ikke tildelt!</div>
            </template>
          </title-slot>
          <title-slot class="mt-6" title="Ønskeliste">
            <template v-if="selected.wishlist.length > 0">
              <ol class="ml-4 list-decimal">
                <li 
                  :class="{ 'text-red-500': wish.pharmacyId === selectedPharmacyId }"
                  v-for="(wish, windex) in selected.wishlist" :key="windex"
                >{{ wish.name }}</li>
              </ol>
            </template>
            <template v-else>
              Ingen ønskeliste!
            </template>
          </title-slot>
        </div>

      </div>

      <div class="mt-6 flex justify-between">
        <button class="btn" @click.prevent="onDeleteClick">Student har sluttet</button>
        <button class="btn ml-2" @click.prevent="onSelectOtherClick">{{ selected.pharmacy ? 'Bytt' : 'Velg' }} praksisapotek</button>
      </div>

      <div class="mt-6" v-if="selectOtherVisible">
        <input class="w-full mb-4" type="text" v-model="filter" placeholder="Søk" />
        <div :class="{ 'bg-gray-200': index % 2 === 0 }" v-for="(slot, index) in filteredSlots" :key="index">
          <div class="flex items-center justify-between p-2">
            <button @click="onToggle(slot)" class="text-red-500 focus:outline-none">
              {{ slot.name }} <font-awesome-icon :icon="['far',  slot.pharmacy ? 'chevron-up' : 'chevron-down']"/>
            </button>
            <button class="btn text-sm" @click.prevent="onSelectSlot(slot)">Velg</button>
          </div>
          
          <div class="text-sm p-2" v-if="slot.pharmacy">

            <div class="inline-flex items-center mr-2" v-if="slot.pharmacy.contactName">
              <font-awesome-icon :icon="['far', 'user']"/>
              <div class="ml-1">{{ slot.pharmacy.contactName }}</div>
            </div>

            <div class="inline-flex items-center mr-2" v-if="slot.pharmacy.contactPhone">
              <font-awesome-icon :icon="['far', 'phone']"/>
              <div class="ml-1">{{ slot.pharmacy.contactPhone }}</div>
            </div>

            <div class="flex items-center mr-2" v-if="slot.pharmacy.address || slot.pharmacy.postCode || slot.pharmacy.postPlace">
              <font-awesome-icon :icon="['far', 'map-marker']"/>
              <div class="ml-1">{{ slot.pharmacy.address ? slot.pharmacy.address + ', ': '' }} {{ slot.pharmacy.postCode }} {{ slot.pharmacy.postPlace }}</div>      
            </div>

            <div class="mt-4 h-56" v-if="slot.pharmacy.latitude > 0">
              <l-map 
                ref="map"           
                :zoom="slot.pharmacy.zoom"
                :center="[slot.pharmacy.latitude, slot.pharmacy.longitude]"
                :options="{ scrollWheelZoom: false }"
              >
                <l-tile-layer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"></l-tile-layer>
                <l-marker :lat-lng="[slot.pharmacy.latitude, slot.pharmacy.longitude]"></l-marker>
              </l-map>
            </div>
            
            <div class="my-4" v-if="slot.pharmacy.description">
              <div v-html="slot.pharmacy.description"></div>
            </div>

            <img v-if="slot.pharmacy.image" :src="slot.pharmacy.image" class="w-full mb-4" />
            
          </div>
        </div>
      </div>

    </modal>
    
  </div>
</template>

<script>
import api from '@/api'
import _ from 'lodash'
export default {
  name: 'pk-results',
  data () {
    return {
      gotSlot: null,
      electionPeriod: null,
      selected: null,
      headers: [
        { text: 'Fornavn', align: 'left', value: 'student.firstName' },
        { text: 'Etternavn', align: 'left', value: 'student.lastName' },
        { text: 'E-post', align: 'left', value: 'student.email' },
        { text: 'Praksisperiode', align: 'left', value: 'practicePeriod.name' },
        { text: 'Apotek', align: 'left', value: 'pharmacy.name' },
        { text: 'Ø', align: 'left', value: 'priority' },
        { text: '#', align: 'left', value: 'student.position' },
      ],
      items: [],
      selectOtherVisible: false,
      availablePracticeSlots: [],
      filter: ''
    }
  },
  computed: {
    filteredItems () {
      return _.filter(this.items, item => {
        return this.gotSlot === null ||
        (this.gotSlot === true && item.pharmacy !== null) ||
        (this.gotSlot === false && item.pharmacy === null)
      })
    },
    selectedPharmacyId () {
      return _.get(this.selected, "pharmacy.pharmacyId", "")
    },
    filteredSlots () {
      return this.filter == ''
      ? this.availablePracticeSlots
      : _.filter(this.availablePracticeSlots, s => s.name.toLowerCase().indexOf(this.filter.toLowerCase()) >= 0)
    }
  },
  methods: {
    onClick (item) {
      this.selected = item
    },
    onExport () {
      api.pk.export()
    },
    onExportAvailable () {
      api.pk.exportAvailable()
    },
    async onDeleteClick () {
      if (confirm("Er du sikker på at du vil slette denne studenten?")) {
        await api.pk.deleteStudent(this.selected.student.studentId)
        this.items = await api.pk.getPracticeSlots()
        this.selected = null
      }
    },
    async onSelectOtherClick () {      
      this.selectOtherVisible = !this.selectOtherVisible
      if (this.selectOtherVisible) {
        this.availablePracticeSlots = await api.pk.getAvailablePracticeSlots(
          this.selected.practicePeriod.study, 
          this.selected.student.studentId
        )
      }
    },
    async onSelectSlot (slot) {
      await api.pk.assignPracticeSlot(this.selected.student.studentId, slot.practiceSlotId)
      this.items = await api.pk.getPracticeSlots()
      this.selected = _.find(this.items, item => item.student.studentId === this.selected.student.studentId)
      this.selectOtherVisible = false
    },
    async onToggle (slot) {      
      if (!slot.pharmacy) {
        this.$set(slot, "pharmacy", await api.pk.getPharmacy(slot.pharmacyId))
      } else {
        slot.pharmacy = null
      }
    }
  },
  async created () {

    const [electionPeriod, items] = await Promise.all([
      api.pk.getElectionPeriod(),
      api.pk.getPracticeSlots()
    ])

    this.electionPeriod = electionPeriod 
    this.items = items 
  }
}
</script>